import React, { useState } from "react";
import pluralize from "pluralize";
import { DiamondIcon as QuaddieTitleIcon } from "@picklebet/ui";
import { triggerHaptic } from "@/utilities/haptics";
import { constructCurrency } from "common/currency";
import { rounded } from "@/utilities/exoticStake";
import { useFiatCurrency } from "hooks/firestore/user/useFiatCurrency";

import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useSetSingleStake } from "@/features/betslip/hooks/useStakeActions";
import { useRemoveSelection } from "@/features/betslip/hooks/useBetslipActions";
import { useRemainingPromoBalance } from "@/features/betslip/hooks/useRemainingPromoBalance";
import type { QuaddieSelection } from "@/features/betslip/types";
import {
  countQuaddieCombos,
  getQuaddieTitle,
} from "@/features/betslip/utils/selectionHelpers";
import { BetCard } from "@/features/betslip/components/cards/BetCard";
import { SelectionAlert } from "@/features/betslip/components/parts/SelectionAlert";
import { SelectionEventStatus } from "@/features/betslip/components/parts/SelectionEventStatus";
import { BetCardHeader } from "@/features/betslip/components/parts/BetCardHeader";
import { RemoveButton } from "@/features/betslip/components/parts/RemoveButton";
import { StakeInput } from "@/features/betslip/components/parts/StakeInput";
import { QuickStakeChips } from "@/features/betslip/components/parts/QuickStakeChips";
import { BetRow } from "@/features/betslip/components/parts/BetRow";
import { BetSpine } from "@/features/betslip/components/parts/BetSpine";
import { ComboGroup } from "@/features/betslip/components/parts/ComboGroup";
import { PayoutValue } from "@/features/betslip/components/parts/PayoutValue";
import { StakeStrip } from "@/features/betslip/components/parts/StakeStrip";
import { PromoPills } from "@/features/betslip/components/parts/tokens/PromoPills";

import * as styles from "@/features/betslip/components/cards/QuaddieSelectionCard.module.scss";

type QuaddieSelectionCardProps = {
  outcomeId: string;
  selection: QuaddieSelection;
};

export const QuaddieSelectionCard = ({
  outcomeId,
  selection,
}: QuaddieSelectionCardProps) => {
  const stake = useBetslipStore((s) => s.stakes.singles[outcomeId] ?? 0);
  const isPlacing = useBetslipStore((s) => s.isPlacing);
  const isStakeDisabled = useBetslipStore(
    (s) => s.stakeDisabled[outcomeId] ?? false,
  );
  const isPromo = useBetslipStore(
    (s) => s.promoCash.singles[outcomeId] ?? false,
  );
  const setSingleStake = useSetSingleStake();
  const removeSelection = useRemoveSelection();
  const maxPromoStakeCents = useRemainingPromoBalance({
    type: "single",
    outcomeId,
  });
  const fiatCurrency = useFiatCurrency();
  const [hasFocus, setHasFocus] = useState(false);

  const title = getQuaddieTitle(selection.quaddieType);
  const comboCount = countQuaddieCombos(
    selection.quaddieSelections as Record<string, { outcomeId: string }[]>,
  );
  const meetingName = selection.marketName.replace(
    /\s+\d{2}\/\d{2}\/\d{4}$/,
    "",
  );
  // Mirrors legacy QuaddieBetSummary: sub-cent per-combo stakes show <$0.01,
  // the rest go through the shared currency formatter.
  const perComboCents = comboCount ? stake / comboCount : 0;
  const perComboDisplay =
    perComboCents < 1 && perComboCents > 0
      ? "<$0.01"
      : constructCurrency(rounded(perComboCents), {
          currencyType: fiatCurrency,
        });

  // One row per race leg, in race order.
  const races = Object.values(selection.quaddieSelections)
    .filter((runners) => runners.length > 0)
    .sort((a, b) => a[0].raceNumber - b[0].raceNumber);

  const handleRemove = () => {
    triggerHaptic.click();
    removeSelection(outcomeId);
  };

  return (
    <BetCard
      testId={`selection-${outcomeId}`}
      alert={<SelectionAlert outcomeId={outcomeId} placement="card" />}
    >
      <BetCardHeader
        icon={<QuaddieTitleIcon />}
        title={meetingName}
        subtitle={title}
        clamp={<SelectionEventStatus selection={selection} />}
        rightSlot={
          <RemoveButton
            onClick={handleRemove}
            disabled={isPlacing}
            testId={`remove-${outcomeId}`}
          />
        }
        testId={`header-quaddie-${outcomeId}`}
      />
      <SelectionAlert outcomeId={outcomeId} placement="outcome" />
      <ComboGroup
        variant="quaddie"
        label={pluralize("Combo", comboCount, true)}
        testId={`quaddie-combos-${outcomeId}`}
      >
        <BetSpine padded>
          {races.map((runners) => (
            <BetRow
              key={runners[0].raceNumber}
              icon={
                <span className={styles.raceBadge}>
                  R{runners[0].raceNumber}
                </span>
              }
              name={runners
                .map((runner) => runner.runnerNumber)
                .sort((a, b) => a - b)
                .join(", ")}
              testId={`quaddie-race-${outcomeId}-${runners[0].raceNumber}`}
            />
          ))}
        </BetSpine>
      </ComboGroup>
      <StakeStrip
        testId={`quaddie-stake-strip-${outcomeId}`}
        promoActive={isPromo}
        labels={["Total Stake", "Stake/Combo", "Est. Payout"]}
        cellVariants={["field", "plain", "plain"]}
        leftSlot={
          <StakeInput
            value={stake}
            onChange={(cents) => setSingleStake(outcomeId, cents)}
            disabled={isStakeDisabled}
            testId={`stake-input-${outcomeId}`}
            onFocus={() => setHasFocus(true)}
            onBlur={() => setHasFocus(false)}
          />
        }
        middleSlot={
          <span data-testid="quaddie-per-combo">{perComboDisplay}</span>
        }
        rightSlot={<PayoutValue dollars={0} tbd testId="quaddie-payout" />}
        quickStake={
          <QuickStakeChips
            isOpen={hasFocus && !isStakeDisabled}
            valueCents={stake}
            onSetCents={(cents) => setSingleStake(outcomeId, cents)}
            isPromo={isPromo}
            maxPromoStakeCents={maxPromoStakeCents}
            arrowOffset="calc(-50%)"
            detached
          />
        }
      />
      <PromoPills
        outcomeIds={[outcomeId]}
        isMulti={false}
        selection={selection}
      />
    </BetCard>
  );
};
